import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSettings } from '../../hooks';
import { useToast } from '../../context/ToastContext';
import {
  SESSION_CALCULATORS,
  clearActivity,
  clearInputs,
  isIdleExpired,
  loadDefaults,
  loadRecents,
  markActivity,
  newRecentId,
  pushRecent,
  readSharedSettings,
  readValues,
  sameCalculation,
  saveDefaults,
  saveRecents,
  visibleRecents,
  writeValues,
  type RecentCalculation,
} from './calcSession';
import { CalcSessionContext, type CalcSessionContextValue } from './calcSessionContext';

/** A calculation is added to recents once its inputs have sat unchanged this long. */
export const RECORD_SETTLE_MS = 4000;
/** How often an open page checks whether it has gone idle. */
const IDLE_CHECK_MS = 15000;
/** Throttle for writing the last-input time on every keystroke. */
const ACTIVITY_WRITE_MS = 2000;

export interface CalculatorSessionProps {
  /** Calculator section id; must be a key of SESSION_CALCULATORS. */
  id: string;
  /** Page name for the reset notice. */
  title: string;
  children: ReactNode;
}

/**
 * Wraps one calculator page: resets it to its defaults after 10 minutes with no
 * input (counted while the page is closed too) and keeps its recent
 * calculations, which RecentCalculations lists under the results banner.
 *
 * Inputs are not touched here directly: the page reads them from localStorage
 * when it mounts, so a reset or a reload clears or rewrites the stored values
 * and remounts the page.
 */
export function CalculatorSession({ id, title, children }: CalculatorSessionProps) {
  const cfg = SESSION_CALCULATORS[id];
  const { settings, updateSettings } = useSettings();
  const { showToast } = useToast();

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Idle since the last visit: clear before the page's first render reads its inputs.
  // The calculation being cleared was already saved to recents when it settled.
  const [resetOnOpen] = useState(() => {
    if (!isIdleExpired(id)) return false;
    clearInputs(cfg);
    clearActivity(id);
    return true;
  });
  // The page is about to mount with its defaults (just reset, or never used).
  const freshRef = useRef(resetOnOpen || Object.keys(readValues(cfg)).length === 0);

  const [generation, setGeneration] = useState(0);
  const [allRecents, setAllRecents] = useState<RecentCalculation[]>(() => loadRecents(id));
  const [now, setNow] = useState(() => Date.now());

  /** Inputs changed by the user since the page last mounted. */
  const dirtyRef = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityWrite = useRef(0);
  const summaryRef = useRef({ label: '', result: '' });

  // Once the page has mounted with its defaults (its own effects have written
  // them back), remember them so an untouched page is never listed as a recent.
  useEffect(() => {
    if (!freshRef.current) return;
    freshRef.current = false;
    saveDefaults(id, { values: readValues(cfg), settings: readSharedSettings(cfg, settingsRef.current) });
  }, [id, cfg, generation]);

  const record = useCallback(() => {
    if (settleTimer.current) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    const current = { values: readValues(cfg), settings: readSharedSettings(cfg, settingsRef.current) };
    if (Object.keys(current.values).length === 0) return;
    const defaults = loadDefaults(id);
    if (defaults && sameCalculation(defaults, current)) return;
    const entry: RecentCalculation = {
      id: newRecentId(),
      at: Date.now(),
      label: summaryRef.current.label,
      result: summaryRef.current.result,
      ...current,
    };
    const next = pushRecent(loadRecents(id), entry);
    saveRecents(id, next);
    setAllRecents(next);
  }, [id, cfg]);

  const noteActivity = useCallback(() => {
    const t = Date.now();
    if (t - lastActivityWrite.current >= ACTIVITY_WRITE_MS) {
      lastActivityWrite.current = t;
      markActivity(id, t);
    }
    dirtyRef.current = true;
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(record, RECORD_SETTLE_MS);
  }, [id, record]);

  const loadRecent = useCallback((recentId: string) => {
    record();
    const recent = loadRecents(id).find(r => r.id === recentId);
    if (!recent) return;
    writeValues(cfg, recent.values);
    if (recent.settings) updateSettings(recent.settings);
    markActivity(id);
    lastActivityWrite.current = Date.now();
    dirtyRef.current = false;
    // Now the newest: it is what an idle reset would clear next.
    const next = pushRecent(loadRecents(id), { ...recent, at: Date.now() });
    saveRecents(id, next);
    setAllRecents(next);
    setGeneration(g => g + 1);
  }, [id, cfg, record, updateSettings]);

  const notifyReset = useCallback(() => {
    const latest = loadRecents(id)[0];
    showToast(
      `${title} was reset to its defaults after 10 minutes with no input.`,
      'info',
      8000,
      latest ? { label: 'Bring back', onClick: () => loadRecent(latest.id) } : undefined,
    );
  }, [id, title, showToast, loadRecent]);

  // Coming back after 10 minutes: say why the page is blank.
  useEffect(() => {
    if (resetOnOpen) notifyReset();
    // Once, for the reset done before the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While open: tick the "min ago" clock and reset once the page goes idle.
  useEffect(() => {
    const check = () => {
      setNow(Date.now());
      if (!isIdleExpired(id)) return;
      record();
      clearInputs(cfg);
      clearActivity(id);
      freshRef.current = true;
      setAllRecents(loadRecents(id));
      setGeneration(g => g + 1);
      notifyReset();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') record();
      else check();
    };
    const timer = setInterval(check, IDLE_CHECK_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', record);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', record);
    };
  }, [id, cfg, record, notifyReset]);

  // Leaving the page (another calculator): save what was being worked on.
  useEffect(() => () => record(), [record]);

  const describeCurrent = useCallback((label: string, result: string) => {
    summaryRef.current = { label, result };
  }, []);

  const current = useMemo(
    () => ({ values: readValues(cfg), settings: readSharedSettings(cfg, settings) }),
    // Re-read when the list changes or the page remounts; the list hides the
    // calculation on screen, which is always the newest one recorded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cfg, settings, allRecents, generation, now],
  );

  const value = useMemo<CalcSessionContextValue>(() => ({
    recents: visibleRecents(allRecents, current),
    now,
    loadRecent,
    describeCurrent,
  }), [allRecents, current, now, loadRecent, describeCurrent]);

  return (
    <CalcSessionContext.Provider value={value}>
      <div
        className="calc-session"
        style={{ display: 'contents' }}
        onInputCapture={noteActivity}
        onChangeCapture={noteActivity}
        onPointerDownCapture={noteActivity}
        onKeyDownCapture={noteActivity}
      >
        <Fragment key={generation}>{children}</Fragment>
      </div>
    </CalcSessionContext.Provider>
  );
}

export default CalculatorSession;
